import { get } from "lodash";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { usePageContextQuery } from "@/shared/page-context";
import { useFormAPI, useFormStore } from "./index";
import type { FormValues } from "./types";
import { yearRangePubSub } from "./yearRangePubSub";

/**
 * Manages the toggle between single-year and year-range mode for an event instance.
 *
 * Publishes mode changes to yearRangePubSub so subscribers (e.g. MAT reconciler)
 * can react without any coupling from this module to theirs.
 *
 * Key behaviors:
 * - Does NOT rewrite date.year on single→range (eliminates drift)
 * - Clears dateProximity on single→range (range mode doesn't use proximity)
 * - On range→single: restores proximity to the configured default (±10 in test arm, Broad in control)
 * - Publishes (fieldPath, mode) after flipping — fire-and-forget
 */
export const useYearRangeToggle = (fieldPath: string) => {
    const { getFieldValue, setFieldValue } = useFormAPI();
    const { data: pageContext } = usePageContextQuery();
    const defaultYearProximity = pageContext.runtimeConfig.defaultYearProximity;

    const yearPath = `${fieldPath}.date.year` as PrefixObjectAccessor<FormValues, []>;
    const endYearPath = `${fieldPath}.date.endYear` as PrefixObjectAccessor<FormValues, []>;
    const rangeModePath = `${fieldPath}.isRangeMode` as PrefixObjectAccessor<FormValues, []>;
    const proximityYearPath = `${fieldPath}.dateProximity.year` as PrefixObjectAccessor<FormValues, []>;
    const proximityMonthPath = `${fieldPath}.dateProximity.month` as PrefixObjectAccessor<FormValues, []>;
    const proximityDayPath = `${fieldPath}.dateProximity.day` as PrefixObjectAccessor<FormValues, []>;

    const isRangeMode = useFormStore(({ values }) => Boolean(get(values, rangeModePath)));

    const toggle = () => {
        const nextMode = !isRangeMode;

        if (nextMode) {
            // Single → Range:
            // Clear proximity — range mode doesn't use it.
            setFieldValue(proximityYearPath, null);
            setFieldValue(proximityMonthPath, null);
            setFieldValue(proximityDayPath, null);
        } else {
            // Range → Single: keep date.year (start) as the single year, clear endYear,
            // and restore proximity to the configured default (AC7.2).
            const startYear = getFieldValue(yearPath) as number | null;
            const endYear = getFieldValue(endYearPath) as number | null;

            if (!startYear && endYear) {
                // If only end year is filled, move it to date.year
                setFieldValue(yearPath, endYear);
            }
            // Clear end year when switching back to single mode
            setFieldValue(endYearPath, null);

            // Restore proximity to the experiment-driven default
            const hasDefault = defaultYearProximity !== null;
            setFieldValue(proximityYearPath, defaultYearProximity);
            setFieldValue(proximityMonthPath, hasDefault ? 0 : null);
            setFieldValue(proximityDayPath, hasDefault ? 0 : null);
        }

        setFieldValue(rangeModePath, nextMode);
        yearRangePubSub.publish(fieldPath, nextMode ? "range" : "single");
    };

    return { isRangeMode, toggle };
};
